import * as React from 'react';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { type LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type TabsVariant = 'pills' | 'tabs';

// Add `variant` property to all child elements
export const injectVariant = <V,>(children: React.ReactNode, variant: V, elements: Array<string>): React.ReactNode => {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const displayName = (child.type as any).displayName ?? (child.type as any).name;

    const wantsVariant = elements.includes(displayName);

    const newProps: Partial<{ variant: V; children?: React.ReactNode }> = {};

    if (wantsVariant && (child.props as any).variant === undefined) {
      newProps.variant = variant;
    }

    if ((child.props as any).children) {
      newProps.children = injectVariant((child.props as any).children, variant, elements);
    }

    return React.cloneElement(child, newProps);
  });
};

interface TabsRootProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
}

const Tabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, TabsRootProps>(
  ({ children, variant, ...props }, ref) => {
    // Default to pills variant if not specified
    const withVariant = injectVariant(children, variant ?? 'pills', [
      TabsList.displayName!,
      TabsTrigger.displayName!,
      TabsContent.displayName!,
    ]);

    return (
      <TabsPrimitive.Root ref={ref} {...props}>
        {withVariant}
      </TabsPrimitive.Root>
    );
  },
);
Tabs.displayName = TabsPrimitive.Root.displayName;

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsVariant;
}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          'flex text-neutral-500 data-[orientation=horizontal]:flex-row data-[orientation=vertical]:flex-col dark:text-neutral-400',
          className,
          {
            'data-[orientation=horizontal]:gap-x-px data-[orientation=vertical]:gap-y-px': variant === 'tabs',
            'items-center rounded-lg bg-neutral-100 p-1 dark:bg-neutral-800': variant === 'pills',
          },
        )}
        {...props}
      />
    );
  },
);
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  icon?: LucideIcon;
  variant?: TabsVariant;
  disabledMessage?: string;
}

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  ({ className, children, icon, variant, disabled, disabledMessage, ...props }, ref) => {
    const tCommon = useTranslations('common');
    const Icon = icon;

    const content = (
      <TabsPrimitive.Trigger
        className={cn(
          className,
          'group/tabs flex w-full text-text-base data-[orientation=vertical]:flex-row data-[orientation=horizontal]:flex-col focus-within:outline-none',
          {
            'data-[orientation=horizontal]:h-20 data-[orientation=horizontal]:min-h-20 data-[orientation=vertical]:min-h-16.25 data-[orientation=horizontal]:gap-y-px data-[orientation=vertical]:gap-x-px':
              variant === 'tabs',
          },
        )}
        ref={ref}
        {...props}
      >
        {variant === 'tabs' && (
          <span className="bg-ui-light group-disabled/tabs:opacity-50 group-data-[orientation=horizontal]/tabs:order-2 group-data-[orientation=horizontal]/tabs:h-2 group-data-[orientation=horizontal]/tabs:min-h-2 group-data-[orientation=horizontal]/tabs:w-full group-data-[orientation=horizontal]/tabs:min-w-full group-data-[orientation=vertical]/tabs:h-full group-data-[orientation=vertical]/tabs:w-2 group-data-[orientation=vertical]/tabs:min-w-2 group-data-[state=active]/tabs:bg-highlight-base" />
        )}
        <div
          className={cn(
            'select-none items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 group-focus-within/tabs:bg-ui-medium! group-disabled/tabs:opacity-50 group-data-[orientation=horizontal]/tabs:h-full group-data-[state=active]/tabs:text-base group-data-[state=active]/tabs:shadow-sm',
            {
              'inline-flex gap-1 rounded-md px-3 py-1 group-data-[state=active]/tabs:bg-highlight-base! group-hover/tabs:group-data-[state=active]/tabs:bg-highlight-hover-color! group-data-[state=active]/tabs:text-text-inverted':
                variant === 'pills',
              'flex w-full flex-col gap-2 bg-ui-panel px-2.25 group-data-[state=active]/tabs:bg-ui-light':
                variant === 'tabs',
              'group-hover/tabs:bg-ui-medium!': !disabled,
            },
          )}
        >
          {Icon && (
            <Icon
              className={cn(variant === 'tabs' ? 'stroke-icon-base icon-5' : 'stroke-text-base icon-4')}
              strokeWidth={variant === 'tabs' ? 2 : 1.5}
            />
          )}
          {children}
        </div>
      </TabsPrimitive.Trigger>
    );

    return disabled ? (
      <Tooltip>
        <TooltipTrigger asChild disabled={disabled}>
          {content}
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="text-balance">
            {disabledMessage ?? tCommon('multiSelectLimitedOptions')}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    ) : (
      content
    );
  },
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => <TabsPrimitive.Content ref={ref} className={cn(className)} {...props} />);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
